import { z } from 'zod';

import { isoDatetimeToDate, isoDateToDate } from './dates';
import { fixture } from './fixtures';
import { NominatedGroup, NominationQuestionSchema, VotableNominees } from './nominations';
import { User, userFixture } from './users';

/**
 * Voting window status. Mirrors `Nominations::VotingSettings::STATUSES` on the
 * backend. `disabled` is never returned by the voting view endpoint (a disabled
 * period 403s), but we list it for completeness in case it appears in future
 * APIs.
 */
export const VotingStatus = z.enum(['disabled', 'upcoming', 'open', 'closed']);
export type VotingStatus = z.infer<typeof VotingStatus>;

/**
 * One anonymized response payload for a single nomination question on a single
 * nominee. The `renderKind` discriminator decides the shape:
 *
 *   - `text`: bare anonymized strings (no nominator attribution)
 *   - `choice`: pre-aggregated counts ready to feed into the bar chart
 */
export const TextAnswer = z.object({
	value: z.string(),
	// The date the nomination carrying this answer was submitted. Anonymized
	// (no nominator identity) but shown above the response so voters get a
	// sense of recency. Nullable because legacy nominations may lack a timestamp.
	date: isoDatetimeToDate.nullable(),
});
export type TextAnswer = z.infer<typeof TextAnswer>;

export const TextQuestionResponse = z.object({
	questionId: z.string(),
	questionText: z.string(),
	renderKind: z.literal('text'),
	values: z.array(TextAnswer),
});
export type TextQuestionResponse = z.infer<typeof TextQuestionResponse>;

export const ChoiceQuestionResponse = z.object({
	questionId: z.string(),
	questionText: z.string(),
	renderKind: z.literal('choice'),
	choices: z.array(
		z.object({
			value: z.string(),
			count: z.number().int().nonnegative(),
		})
	),
});
export type ChoiceQuestionResponse = z.infer<typeof ChoiceQuestionResponse>;

export const QuestionResponse = z.discriminatedUnion('renderKind', [TextQuestionResponse, ChoiceQuestionResponse]);
export type QuestionResponse = z.infer<typeof QuestionResponse>;

export const VotableNominee = z.object({
	nomineeUniquenessKey: z.string(),
	nominationCount: z.number().int().nonnegative(),
	nominatedUser: User.nullable(),
	nominatedGroup: NominatedGroup.nullable(),
	questionResponses: z.array(QuestionResponse),
});
export type VotableNominee = z.infer<typeof VotableNominee>;

export const UserVote = z.object({
	id: z.string(),
	nomineeUniquenessKey: z.string(),
});
export type UserVote = z.infer<typeof UserVote>;

/**
 * Lightweight summary of a nomination period the current user can vote in.
 * Powers the home-page "voting is active" banner and the voting index page.
 * `hasVoted` is true when the user has already cast at least one vote in the
 * period; the banner drives visibility/routing off the unvoted subset while the
 * index page lists every open eligible period.
 */
export const VotablePeriodSummary = z.object({
	id: z.string(),
	name: z.string(),
	votingStatus: VotingStatus,
	votingStartDate: isoDateToDate.nullable(),
	votingEndDate: isoDateToDate.nullable(),
	nomineeCount: z.number().int().nonnegative(),
	submissionCount: z.number().int().nonnegative(),
	creator: User.nullable(),
	hasVoted: z.boolean(),
});
export type VotablePeriodSummary = z.infer<typeof VotablePeriodSummary>;

export const VotingView = z.object({
	nominationPeriodId: z.string(),
	name: z.string(),
	description: z.string(),
	votingStatus: VotingStatus,
	canCastVotes: z.boolean(),
	votesPerVoter: z.number().int().positive().nullable(),
	votableNominees: z.enum(VotableNominees),
	votingStartDate: isoDateToDate.nullable(),
	votingEndDate: isoDateToDate.nullable(),
	allowSelfVoting: z.boolean(),
	nominees: z.array(VotableNominee),
	questions: z.array(NominationQuestionSchema),
	userVotes: z.array(UserVote),
});
export type VotingView = z.infer<typeof VotingView>;

// Fixtures

export const userVoteFixture = (overrides: Partial<UserVote> = {}): UserVote => ({
	id: fixture.id(),
	nomineeUniquenessKey: `user:${fixture.id()}`,
	...overrides,
});

export const textQuestionResponseFixture = (overrides: Partial<TextQuestionResponse> = {}): TextQuestionResponse => ({
	questionId: fixture.id(),
	questionText: 'Why are you nominating them?',
	renderKind: 'text',
	values: [{ value: 'They consistently show up for the team.', date: fixture.pastDate() }],
	...overrides,
});

export const choiceQuestionResponseFixture = (overrides: Partial<ChoiceQuestionResponse> = {}): ChoiceQuestionResponse => ({
	questionId: fixture.id(),
	questionText: 'Which value did they exemplify?',
	renderKind: 'choice',
	choices: [
		{ value: 'Curiosity', count: 3 },
		{ value: 'Craft', count: 1 },
	],
	...overrides,
});

export const votableNomineeFixture = (overrides: Partial<VotableNominee> = {}): VotableNominee => ({
	nomineeUniquenessKey: `user:${fixture.id()}`,
	nominationCount: 3,
	nominatedUser: userFixture(),
	nominatedGroup: null,
	questionResponses: [],
	...overrides,
});

export const votablePeriodSummaryFixture = (overrides: Partial<VotablePeriodSummary> = {}): VotablePeriodSummary => ({
	id: fixture.id(),
	name: fixture.name('Nomination Period'),
	votingStatus: 'open',
	votingStartDate: fixture.pastDate(),
	votingEndDate: fixture.futureDate(),
	nomineeCount: 5,
	submissionCount: 12,
	creator: userFixture(),
	hasVoted: false,
	...overrides,
});

export const votingViewFixture = (overrides: Partial<VotingView> = {}): VotingView => ({
	nominationPeriodId: fixture.id(),
	name: fixture.name('Nomination Period'),
	description: fixture.description('nomination period'),
	votingStatus: 'open',
	canCastVotes: true,
	votesPerVoter: 1,
	votableNominees: VotableNominees.Top5,
	votingStartDate: fixture.pastDate(),
	votingEndDate: fixture.futureDate(),
	allowSelfVoting: false,
	nominees: [votableNomineeFixture(), votableNomineeFixture()],
	questions: [],
	userVotes: [],
	...overrides,
});
